/**
 * Features Service
 *
 * Type-safe feature management service using @monorepo/shared schemas
 */

import type { EntityStatus } from '@monorepo/shared/schemas/common'
import {
  type CreateFeature,
  CreateFeatureSchema,
  type DeleteFeatureResponse,
  DeleteFeatureResponseSchema,
  type FeatureQuery,
  type FeatureResponse,
  FeatureResponseSchema,
  type PaginatedFeaturesResponse,
  PaginatedFeaturesResponseSchema,
  type UpdateFeature,
  UpdateFeatureSchema,
} from '@monorepo/shared/schemas/features'
import { del, get, post, put } from '@/lib/api-client'

export const featureService = {
  /**
   * List all features (filtered by query)
   */
  async getFeatures(query?: FeatureQuery): Promise<PaginatedFeaturesResponse> {
    const queryParams = new URLSearchParams()

    if ((query as Record<string, unknown>)?.feature_type)
      queryParams.append(
        'feature_type',
        String((query as Record<string, unknown>).feature_type)
      )
    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.external_id) queryParams.append('external_id', query.external_id)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/features${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedFeaturesResponseSchema)
  },

  /**
   * Get feature by ID (includes signal associations and value metrics)
   */
  async getFeature(id: string): Promise<FeatureResponse> {
    return get(`/features/${id}`, FeatureResponseSchema)
  },

  /**
   * Create new feature
   */
  async createFeature(data: CreateFeature): Promise<FeatureResponse> {
    const validated = CreateFeatureSchema.parse(data)

    return post('/features', validated, FeatureResponseSchema)
  },

  /**
   * Update feature
   */
  async updateFeature(
    id: string,
    data: UpdateFeature
  ): Promise<FeatureResponse> {
    const validated = UpdateFeatureSchema.parse(data)

    return put(`/features/${id}`, validated, FeatureResponseSchema)
  },

  /**
   * Delete feature
   */
  async deleteFeature(id: string): Promise<DeleteFeatureResponse> {
    return del(`/features/${id}`, DeleteFeatureResponseSchema)
  },

  /**
   * Update feature status
   */
  async updateStatus(
    id: string,
    status: EntityStatus
  ): Promise<FeatureResponse> {
    const validated = UpdateFeatureSchema.parse({ status })

    return put(`/features/${id}`, validated, FeatureResponseSchema)
  },
}
